<script setup lang="ts">
import { GitCompare, Loader2, ZoomIn, ZoomOut } from 'lucide-vue-next'
import { collectHighlightTextNodes, findDocxHighlightSegments, overlayBox } from '~/lib/docxHighlightSearch'
import { annotateSegments, isWhollyDeleted, type DocxSegmentText } from '~/utils/docxSegmentMap'
import { usePersistedPref } from '~/composables/usePersistedPref'
import type { DocxSegmentsPayload } from '#shared/items/docxSegments'

const { t } = useI18n()
/**
 * A new `src` is a new document: callers key this component on it (`:key="src"`), so it remounts
 * rather than watching the prop.
 */
const props = defineProps<{
  src: string
  /**
   * `GET /api/items/[id]/segments` for this file, when it is a stored item: what `scrollToSegment`
   * maps `[pN]` with (MAR-1097). Fetched on the first request to go to a paragraph, never on open.
   */
  segmentsSrc?: string
}>()

const emit = defineEmits<{
  'text-selected': [text: string]
}>()

const containerRef = ref<HTMLDivElement | null>(null)
const wrapperRef = ref<HTMLDivElement | null>(null)
const loading = ref(true)
const error = ref<string | null>(null)
const scale = ref(100)
// Once the user has zoomed by hand, their choice wins — auto-fit only runs for the initial render
// and for a container that GROWS or SHRINKS from under them (the split preview panel is resizable),
// not every layout tick.
const userAdjustedZoom = ref(false)
/**
 * Tracked changes (`<ins>`/`<del>`) and comments shown in the page (MAR-1097). Off is the clean read:
 * insertions shown, deletions hidden — the document as it would read with every change accepted.
 *
 * The page is rendered ONCE with both in it, and this only switches the `docx-changes-shown` class
 * the stylesheet below reads (MAR-1253). Rendering again on each toggle refetched the file, put the
 * spinner over a blank page and lost the reader's place.
 */
const showChanges = usePersistedPref('docx.showChanges', false, raw => raw === '1', { serialize: v => (v ? '1' : '0') })

function toggleChanges() {
  return setShowChanges(!showChanges.value)
}

/**
 * Shows or hides the changes and keeps the reader where they were: the paragraph a finding went to
 * stays in view; with none, the paragraph at the top of the view stays at the same height. A pixel
 * offset would not do — the deleted text appearing or going above it moves everything below.
 */
async function setShowChanges(on: boolean) {
  const anchor = focusedIndex === null ? topVisibleBlock() : null
  showChanges.value = on
  await nextTick()
  const focused = wrapperRef.value?.querySelector<HTMLElement>('.docx-segment-focus')
  if (focused) {
    focused.scrollIntoView({ block: 'center' })
  } else if (anchor && containerRef.value) {
    containerRef.value.scrollTop += anchor.el.getBoundingClientRect().top - anchor.top
  }
}

/** The first paragraph or table row with height that reaches into the view, and where it is. */
function topVisibleBlock(): { el: HTMLElement; top: number } | null {
  if (!containerRef.value || !wrapperRef.value) return null
  const viewTop = containerRef.value.getBoundingClientRect().top
  for (const el of wrapperRef.value.querySelectorAll<HTMLElement>('p, tr')) {
    const r = el.getBoundingClientRect()
    if (r.height > 0 && r.bottom > viewTop) return { el, top: r.top }
  }
  return null
}

/** The render in flight or last finished: anything that reads the page waits on it. */
let rendered: Promise<void> = Promise.resolve()

async function loadDocx() {
  loading.value = true
  error.value = null

  try {
    // Fetch raw DOCX bytes. Deliberately a bare `fetch` and NOT `useApi().apiStreamFetch`:
    // `/api/items/[id]/file` 302s to a signed GCS URL, and any non-safelisted request header
    // (`Authorization`, `X-Invite-Code`) forces the browser to preflight that cross-origin leg.
    // The bucket's CORS config allows GET/HEAD only — it doesn't list those headers — so the
    // OPTIONS comes back without `Access-Control-Allow-Origin` and the whole load fails.
    // `credentials: 'same-origin'` sends the HttpOnly `__session` cookie to our API (which
    // server/middleware/auth.ts accepts as a fallback) and sends nothing to GCS. Same reason
    // PdfViewer hands the bare URL to pdf.js.
    const response = await fetch(props.src, { credentials: 'same-origin' })
    // Carry the status on the error rather than baking it into a message string: the catch maps it
    // to something a person can act on, and `HTTP 404` is not that.
    if (!response.ok) throw Object.assign(new Error(`HTTP ${response.status}`), { status: response.status })
    const blob = await response.blob()

    // Render into container
    const { renderAsync } = await import('docx-preview')

    if (!wrapperRef.value) return
    wrapperRef.value.innerHTML = ''

    await renderAsync(blob, wrapperRef.value, undefined, {
      className: 'docx-rendered',
      inWrapper: true,
      ignoreWidth: false,
      ignoreHeight: false,
      ignoreFonts: false,
      // Visible page breaks (each page its own `.docx-rendered` section, docx-preview's own default
      // gap between them) — a multi-page contract with no visual break read as one seamless wall of
      // text with no sense of where a page actually ends.
      breakPages: true,
      ignoreLastRenderedPageBreak: true,
      experimental: true,
      // Always drawn; `docx-changes-shown` decides whether they show (see `showChanges`).
      renderChanges: true,
      renderComments: true,
    })
    userAdjustedZoom.value = false
    await nextTick()
    fitToWidth()
  } catch (err: any) {
    // The raw exception message used to be the user-facing string. That is how this component
    // reported a dropped connection as the bare words "Failed to fetch" — which named neither the
    // cause nor the layer, and sent a bug report at the storage path instead of the server that had
    // just restarted (MAR-455). The detail belongs in the console; the panel gets a sentence.
    console.error('[DocxViewer] Failed to load DOCX:', { src: props.src, status: err?.status, err })
    error.value = viewerErrorMessage(err, t)
  } finally {
    loading.value = false
  }
}

/**
 * Scales the page down to fit the container's actual width, rather than always starting at a
 * hardcoded 100% — `docx-preview` renders at the document's real page width (a Letter/A4 page,
 * 800px+), which is wider than the split preview panel beside chat, so 100% cut the page off on the
 * right with no visual cue that it was even scrollable. `scrollWidth` on the wrapper is unaffected by
 * its own `transform: scale()` (CSS transforms don't change layout metrics), so it's read here as the
 * document's untransformed natural width regardless of the current zoom.
 */
function fitToWidth() {
  if (userAdjustedZoom.value || !wrapperRef.value || !containerRef.value) return
  // The real page (`.docx-rendered`), not `wrapperRef.scrollWidth` — that also counts
  // `.docx-rendered-wrapper`'s own ~30px side padding, which isn't part of what a person perceives as
  // "the page" and was making the fit undershoot, leaving a wide gray gutter that had nothing to do
  // with the small buffer below.
  const page = wrapperRef.value.querySelector<HTMLElement>('.docx-rendered')
  const naturalWidth = page?.offsetWidth || wrapperRef.value.scrollWidth
  const available = containerRef.value.clientWidth - 8 // a small cosmetic gutter, not a second margin
  if (naturalWidth <= 0 || available <= 0) return
  scale.value = Math.min(100, Math.max(40, Math.floor((available / naturalWidth) * 100)))
  // The page's own left margin must be what sits at the container's left edge. `transform: scale()`
  // doesn't change the wrapper's OWN layout width (only how it paints), so the container's scrollable
  // area is sized for the document's real, unscaled width — and a container that was scrolled at a
  // previous (larger) scale keeps whatever `scrollLeft` it had, now pointing partway into a page that
  // visually shrank out from under it. Every line then loses however many characters that offset
  // covers, uniformly — exactly the "page shifted left" symptom this fixes.
  containerRef.value.scrollLeft = 0
  containerRef.value.scrollTop = 0
}

// ── Text selection ──
function handleMouseUp() {
  const selection = window.getSelection()
  if (!selection || selection.isCollapsed) return
  const text = selection.toString().trim()
  if (text) emit('text-selected', text)
}

// ── Zoom ──
function zoomIn() {
  userAdjustedZoom.value = true
  scale.value = Math.min(scale.value + 15, 200)
}

function zoomOut() {
  userAdjustedZoom.value = true
  scale.value = Math.max(scale.value - 15, 50)
}

function resetZoom() {
  userAdjustedZoom.value = false
  fitToWidth()
}

// ── Highlight text ──
const highlightEls = ref<HTMLElement[]>([])

/** Every mark on the page: the text overlays and the paragraph a finding went to. */
function clearHighlights() {
  for (const el of highlightEls.value) el.remove()
  highlightEls.value = []
  focusedIndex = null
  for (const el of wrapperRef.value?.querySelectorAll('.docx-segment-focus') ?? []) el.classList.remove('docx-segment-focus')
}

// Rendered document text only — docx-preview's injected `<style>` blocks are not (MAR-382).
function getAllTextNodes(root: Node): Text[] {
  return collectHighlightTextNodes(root).filter(node => node.data.trim())
}

// `page` is ignored for DOCX: see app/lib/docxHighlightSearch.ts.
async function highlightText(text: string) {
  if (!wrapperRef.value) return false
  clearHighlights()

  const segments = findDocxHighlightSegments(wrapperRef.value, text)
  if (!segments?.length) return false

  // Create highlight overlays using Range for precise positioning — exactly the matched characters
  for (const { node, start, end } of segments) {
    const range = document.createRange()
    range.setStart(node, start)
    range.setEnd(node, end)
    const rects = range.getClientRects()
    const containerRect = wrapperRef.value.getBoundingClientRect()

    for (const rect of rects) {
      if (rect.width === 0 || rect.height === 0) continue

      const box = overlayBox(rect, containerRect, scale.value / 100)
      const highlight = document.createElement('div')
      highlight.className = 'docx-highlight'
      highlight.style.position = 'absolute'
      highlight.style.left = `${box.left + wrapperRef.value.scrollLeft}px`
      highlight.style.top = `${box.top + wrapperRef.value.scrollTop}px`
      highlight.style.width = `${box.width}px`
      highlight.style.height = `${box.height}px`
      highlight.style.backgroundColor = 'color-mix(in srgb, var(--color-warning) 40%, transparent)'
      highlight.style.borderBottom = "2px solid var(--color-warning-ink)"
      highlight.style.borderRadius = '2px'
      highlight.style.pointerEvents = 'none'
      highlight.style.boxShadow = '0 0 0 1px color-mix(in srgb, var(--color-warning) 30%, transparent)'
      highlight.style.animation = 'highlight-pulse 1.5s ease-in-out'
      highlight.style.zIndex = '10'

      wrapperRef.value.appendChild(highlight)
      highlightEls.value.push(highlight)
    }
  }

  // Scroll first matching node into view
  segments[0]?.node.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  return true
}

// ── Test ──
function selectRandomLines() {
  if (!wrapperRef.value) return
  const textNodes = getAllTextNodes(wrapperRef.value)
  if (textNodes.length === 0) return

  const start = Math.floor(Math.random() * Math.max(1, textNodes.length - 3))
  const end = Math.min(start + 1 + Math.floor(Math.random() * 3), textNodes.length - 1)

  const range = document.createRange()
  range.setStart(textNodes[start]!, 0)
  range.setEnd(textNodes[end]!, textNodes[end]!.textContent?.length || 0)

  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(range)

  const text = selection?.toString().trim()
  if (text) emit('text-selected', text)
}

// ── Go to a paragraph [pN] (MAR-1097) ──
let segments: DocxSegmentText[] | null = null
/** The paragraph `scrollToSegment` last marked, so a re-render can mark it again. */
let focusedIndex: number | null = null

async function ensureSegmentsMapped(): Promise<boolean> {
  if (!wrapperRef.value || !props.segmentsSrc) return false
  // Asked while the page is still rendering: mapping now would map an empty wrapper.
  await rendered
  if (!segments) {
    const payload = await useApi().apiFetch<DocxSegmentsPayload>(props.segmentsSrc)
    segments = payload.segments
    annotateSegments(wrapperRef.value, segments)
  }
  return true
}

/**
 * Scrolls to the paragraph `/v1/extract` numbers `index` and marks it. A paragraph the render cannot
 * be matched to falls back to highlighting `fallbackText` (the finding's quoted clause, say).
 * A paragraph deleted whole is only on the page with changes shown, so going to one turns them on.
 * Resolves whether it went anywhere.
 */
async function scrollToSegment(index: number, fallbackText?: string): Promise<boolean> {
  let target: HTMLElement | null = null
  try {
    if (await ensureSegmentsMapped()) {
      const segment = segments!.find(s => s.index === index)
      // Hidden, it is in the page but takes no room: show the changes so there is something to see.
      if (segment && isWhollyDeleted(segment) && !showChanges.value) await setShowChanges(true)
      target = wrapperRef.value!.querySelector<HTMLElement>(`[data-segment-index="${index}"]`)
    }
  } catch (err) {
    console.error('[DocxViewer] could not read the paragraphs to map', { src: props.segmentsSrc, err })
  }
  if (target) {
    clearHighlights()
    focusedIndex = index
    target.classList.add('docx-segment-focus')
    target.scrollIntoView({ behavior: 'smooth', block: 'center' })
    return true
  }
  if (!fallbackText) {
    clearHighlights()
    return false
  }
  return highlightText(fallbackText)
}

// `isReady` — rendered (or failed): the docs page waits on it before applying an agent highlight (MAR-382).
defineExpose({ isReady: () => !loading.value, highlightText, clearHighlights, selectRandomLines, scrollToSegment })

onMounted(() => { rendered = loadDocx() })

// Re-fits when the panel itself resizes — most relevantly the split preview panel beside chat,
// which the user can drag wider/narrower. Only while they haven't zoomed by hand (`fitToWidth`'s own
// guard); dragging the panel must not fight a deliberate zoom choice.
let resizeObserver: ResizeObserver | null = null
onMounted(() => {
  if (!containerRef.value) return
  resizeObserver = new ResizeObserver(() => fitToWidth())
  resizeObserver.observe(containerRef.value)
})
onUnmounted(() => resizeObserver?.disconnect())
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Toolbar -->
    <div class="flex items-center justify-between px-2 py-1 border-b shrink-0 bg-muted/30">
      <span class="text-[11px] text-muted-foreground">DOCX</span>

      <div class="flex items-center gap-1">
        <button
          type="button"
          data-testid="docx-toggle-changes"
          :aria-pressed="showChanges ? 'true' : 'false'"
          :title="t('viewer.trackChangesHint')"
          class="h-6 px-1.5 flex items-center gap-1 text-[10px] rounded-md transition-colors hover:bg-accent-lighter"
          :class="showChanges ? 'text-accent-ink bg-accent-surface' : 'text-muted-foreground hover:text-foreground'"
          @click="toggleChanges"
        >
          <GitCompare class="h-3.5 w-3.5" />
          {{ t('viewer.trackChanges') }}
        </button>
        <button
          type="button"
          class="h-6 px-1.5 text-[10px] rounded-md text-muted-foreground hover:text-foreground hover:bg-accent-lighter transition-colors"
          @click="selectRandomLines"
        >
          Test
        </button>
        <button
          type="button"
          class="h-6 w-6 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent-lighter transition-colors"
          @click="zoomOut"
        >
          <ZoomOut class="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          class="h-6 w-10 text-[10px] text-muted-foreground tabular-nums text-center rounded-md hover:text-foreground hover:bg-accent-lighter transition-colors"
          :title="t('viewer.fitToWidth')"
          @click="resetZoom"
        >
          {{ scale }}%
        </button>
        <button
          type="button"
          class="h-6 w-6 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent-lighter transition-colors"
          @click="zoomIn"
        >
          <ZoomIn class="h-3.5 w-3.5" />
        </button>
      </div>
    </div>

    <!-- Scrollable rendered content -->
    <div ref="containerRef" data-ph-block class="flex-1 overflow-auto bg-background" @mouseup="handleMouseUp">
      <!-- Loading -->
      <div v-if="loading" class="h-full flex items-center justify-center">
        <Loader2 class="h-5 w-5 animate-spin text-muted-foreground" />
      </div>

      <!-- Error -->
      <div v-else-if="error" class="h-full flex items-center justify-center px-4">
        <p class="text-sm text-destructive text-center">{{ error }}</p>
      </div>

      <!-- DOCX render target -->
      <div
        ref="wrapperRef"
        class="docx-wrapper relative"
        :class="{ 'docx-changes-shown': showChanges }"
        :style="{ transform: `scale(${scale / 100})`, transformOrigin: 'top left' }"
      />
    </div>
  </div>
</template>

<style>
/* docx-preview injects its own styles; we just ensure the container behaves */
.docx-wrapper {
  min-height: 100%;
  /* The page is paper in either theme: docx-preview paints it white with black ink. The marks this
     viewer draws on it (the paragraph a finding went to, <ins>, <del>, text highlights) take the
     LIGHT-theme values of the tokens they read (tailwind.css `@theme`); the `.dark` ones are built
     for a dark ground and fell to 1.29:1 on the white page. */
  --color-accent-ink: #B84816;
  --color-accent-surface: #FCEEE8;
  --color-success: hsl(137 30% 30%);
  --color-destructive: hsl(350 70% 40%);
  --color-warning: #F8F5C2;
  --color-warning-ink: hsl(56 70% 24%);
}
.docx-wrapper .docx-rendered {
  padding: 1rem;
}
/* docx-preview's own injected `.docx-rendered-wrapper` is a flex column with `align-items: center`,
   which horizontally CENTERS each page — fine in a viewer wider than the page, but this one usually
   isn't (the split preview panel is narrower than a Letter/A4 page's 816px). A centered page wider
   than its container overflows equally on both sides, and the LEFT overflow lands off-screen with no
   scrollbar to reach it (flexbox's default "unsafe" centering, not "safe"), which is what cut every
   line's opening words. `fitToWidth()` in the script also assumes the page's true left edge sits at
   the container's left edge; centering silently broke that assumption regardless of the computed
   scale being exactly right. */
.docx-wrapper .docx-rendered-wrapper {
  align-items: flex-start !important;
  /* Same wrapper also carries its own `30px 30px 0` padding — dead space around the page with no
     background of its own, so it shows the dark container `bg-background` through it. `.docx-rendered`
     just above already has its own `padding: 1rem` for the page's internal margin, which is what makes
     this wrapper's padding purely redundant chrome rather than something the page needs. */
  padding: 0 !important;
}
/* Tracked changes and comments are always in the page; hidden, the page reads as if every change
   were accepted (MAR-1253): deletions and comment markers take no room, insertions are plain text. */
.docx-wrapper:not(.docx-changes-shown) del,
.docx-wrapper:not(.docx-changes-shown) .docx-rendered-comment-ref,
.docx-wrapper:not(.docx-changes-shown) .docx-rendered-comment-popover {
  display: none !important;
}
.docx-wrapper:not(.docx-changes-shown) ins {
  color: inherit;
  text-decoration: none;
}
/* Tracked changes, when shown (MAR-1097). `success` marks what a person changed and is never a
   surface (DESIGN.md), so color and line only, no fill. */
.docx-wrapper ins {
  color: var(--color-success);
  text-decoration: underline;
  text-decoration-color: var(--color-success);
}
.docx-wrapper del {
  color: var(--color-destructive);
  text-decoration: line-through;
  text-decoration-color: var(--color-destructive);
}
/* The paragraph a finding went to. */
.docx-wrapper .docx-segment-focus {
  box-shadow: inset 2px 0 0 var(--color-accent-ink);
  background-color: var(--color-accent-surface);
  animation: highlight-pulse 1.5s ease-in-out;
}
/* Reuse the same highlight animation as PDF viewer */
@keyframes highlight-pulse {
  0% { background-color: color-mix(in srgb, var(--color-warning) 70%, transparent); box-shadow: 0 0 8px color-mix(in srgb, var(--color-warning) 50%, transparent); }
  50% { background-color: color-mix(in srgb, var(--color-warning) 30%, transparent); box-shadow: 0 0 0 1px color-mix(in srgb, var(--color-warning) 30%, transparent); }
  100% { background-color: color-mix(in srgb, var(--color-warning) 40%, transparent); box-shadow: 0 0 0 1px color-mix(in srgb, var(--color-warning) 30%, transparent); }
}
</style>
